import { CARRIER_COORDINATION as T } from "@/constants/testIds";
import {
  FileQuestion, Gavel, FileText, Truck, ShieldCheck, MapPin, PackageCheck,
} from "lucide-react";

const nodes = [
  { icon: FileQuestion, name: "RFQ™", detail: "Structured supplier RFQs" },
  { icon: Gavel, name: "CommodityBid™", detail: "Negotiate & award" },
  { icon: FileText, name: "Purchase Order", detail: "PO issued to supplier" },
  { icon: Truck, name: "FreightIQ™", detail: "Carrier coordination", featured: true },
  { icon: ShieldCheck, name: "Inspection™", detail: "Pre-shipment quality" },
  { icon: MapPin, name: "Live Shipment Tracking™", detail: "Real-time updates" },
  { icon: PackageCheck, name: "Delivered", detail: "Closed & reconciled" },
];

export default function ConnectedWorkspace() {
  return (
    <section id="connected" data-testid={T.connected} className="relative border-t border-slate-line bg-white py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-brand">Connected Workspace</p>
          <h2 className="mt-4 text-3xl font-semibold text-navy sm:text-4xl lg:text-[44px] lg:leading-[1.08]">
            One Workspace. Every Product Connected.
          </h2>
          <p className="mt-5 text-[15px] leading-relaxed text-slate-600">
            FreightIQ™ is a Workspace Product inside DeMaxtore. Every stage of the trade lifecycle — from RFQ to delivery — flows through a single operational spine.
          </p>
        </div>

        <div className="mt-16">
          <div className="hidden lg:block">
            <div className="relative overflow-x-auto">
              <div className="mx-auto flex min-w-full items-stretch justify-between gap-3 pb-2">
                {nodes.map(({ icon: Icon, name, detail, featured }, i) => (
                  <div key={name} className="flex items-center gap-3">
                    <div className={`animate-fade-up stagger-${(i % 6) + 1} relative flex w-[176px] flex-col items-center rounded-2xl border p-5 text-center transition ${
                      featured ? "border-blue-brand bg-white shadow-card-lg" : "border-slate-line bg-white shadow-card"
                    }`}
                    >
                      {featured && (
                        <span className="absolute -top-3 rounded-full bg-blue-brand px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white shadow-card">
                          This Page
                        </span>
                      )}
                      <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${
                        featured ? "bg-blue-soft text-blue-brand" : "bg-slate-50 text-slate-500"
                      }`}
                      >
                        <Icon className="h-5 w-5" strokeWidth={1.8} />
                      </span>
                      <p className={`mt-3 text-[13.5px] font-semibold ${featured ? "text-blue-brand" : "text-navy"}`}>
                        {name}
                      </p>
                      <p className="mt-1 text-[11.5px] leading-snug text-slate-500">{detail}</p>
                    </div>
                    {i < nodes.length - 1 && <span className="text-slate-300">→</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <ol className="relative space-y-3 lg:hidden">
            {nodes.map(({ icon: Icon, name, detail, featured }, i) => (
              <li
                key={name}
                className={`flex items-center gap-4 rounded-2xl border p-4 ${
                  featured ? "border-blue-brand bg-white shadow-card" : "border-slate-line bg-white"
                }`}
              >
                <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                  featured ? "bg-blue-soft text-blue-brand" : "bg-slate-50 text-slate-500"
                }`}
                >
                  <Icon className="h-5 w-5" strokeWidth={1.8} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className={`text-[14px] font-semibold ${featured ? "text-blue-brand" : "text-navy"}`}>{name}</p>
                    {featured && (
                      <span className="rounded-full bg-blue-brand px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white">
                        This page
                      </span>
                    )}
                  </div>
                  <p className="text-[12.5px] text-slate-500">{detail}</p>
                </div>
                <span className="text-[11px] text-slate-400">{String(i + 1).padStart(2, "0")}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
